/**
 * App.jsx
 * Root shell - manages active view state, renders Sidebar + Topbar + view content.
 */

import { useCallback, useEffect, useState } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { isAdmin, getRole } from './data/admins';
import { firstTab, redirectFor } from './data/roles';
import { useMyAccess } from './hooks/useMyAccess';
import { ChatWidget } from './components/chat/ChatWidget';
import SharedViewNotice from './components/tableViews/SharedViewNotice';
import { Sidebar } from './components/layout/Sidebar';
import { Topbar } from './components/layout/Topbar';

import DashboardView from './views/DashboardView';
import ProjectsView from './views/ProjectsView';
import { isActiveProject } from './data/projectStatus';
import EngineersView from './views/EngineersView';
import SparePartsView from './views/SparePartsView';
import CRMView from './views/CRMView';
import LeadsView from './views/LeadsView';
import LeadsDashboardView from './views/LeadsDashboardView';
import OpportunitiesView from './views/OpportunitiesView';
import TasksView from './views/TasksView';
import ReportingView from './views/ReportingView';
import FinanceInvoiceView from './views/FinanceInvoiceView';
import KsaOverviewView from './views/KsaOverviewView';
import LoginView from './views/LoginView';
import AdminUsersView from './views/AdminUsersView';
import ActivityView from './views/ActivityView';
import TicketsView from './views/TicketsView';
import { useOpenTicketCount } from './views/tickets/useOpenTicketCount';
import StaffingCRMView from './views/StaffingCRMView';
import PublicSvmView from './views/PublicSvmView';
import MyScheduleView from './views/engineer/MyScheduleView';
import MyProjectView from './views/engineer/MyProjectView';
import DailyReportView from './views/engineer/DailyReportView';
import ChecklistsView from './views/engineer/ChecklistsView';
import { useMyOpenTaskCount } from './components/tasks/useMyOpenTaskCount';
import { showsOnCards } from './data/resourceGroups';

// AI assistant. Live for every verified role; answers grounded in read-only DB tools
// (server/routes/chat.js) via the Claude Agent SDK using globi's Claude login
// (like GlobalForge — no API key). Needs CLAUDE_CODE_OAUTH_TOKEN on the server;
// until then the widget shows a "not configured" reply. See docs/BLUEPRINT_AICHAT.md.
const CHAT_ENABLED = true;

// Staffing CRM sections as dedicated sidebar tabs (the 'staffing' area in
// src/data/roles.js: by role, or granted in the Team page). Module-level
// wrappers so each map entry is a stable component type.
const CrmDashboard = (props) => <StaffingCRMView section="dashboard" {...props} />;
const CrmAccounts = (props) => <StaffingCRMView section="accounts" {...props} />;
const CrmContacts = (props) => <StaffingCRMView section="contacts" {...props} />;
const CrmLeads = (props) => <StaffingCRMView section="leads" {...props} />;

const VIEWS = {
  dashboard: DashboardView,
  projects: ProjectsView,
  engineers: EngineersView,
  'spare-parts': SparePartsView,
  crm: CRMView,
  leads: LeadsView,
  'leads-dashboard': LeadsDashboardView,
  opportunities: OpportunitiesView,
  tasks: TasksView,
  reporting: ReportingView,
  'finance-invoices': FinanceInvoiceView,
  'ksa-overview': KsaOverviewView,
  login: LoginView,
  team: AdminUsersView,
  activity: ActivityView,
  tickets: TicketsView,
  'crm-dashboard': CrmDashboard,
  'crm-accounts': CrmAccounts,
  'crm-contacts': CrmContacts,
  'crm-leads': CrmLeads,
  // Engineer POV views (Phases 1–3).
  'my-schedule': MyScheduleView,
  'my-project': MyProjectView,
  'daily-reports': DailyReportView,
  checklists: ChecklistsView,
};

const hashToView = () => {
  // Strip any ?query (e.g. #/opportunities?project=bt-0660) before matching the
  // view name, otherwise deep links with params fall through to the dashboard.
  const h = window.location.hash.replace(/^#\/?/, '').split('?')[0];
  return VIEWS[h] ? h : 'dashboard';
};

// Public customer share link: #/svm-view/<token>. Rendered standalone —
// BEFORE the auth gate, no sidebar/topbar, no login. The token is the
// credential (validated server-side by /api/svm-public).
function publicSvmToken() {
  const m = window.location.hash.match(/^#\/svm-view\/([A-Za-z0-9]+)/);
  return m ? m[1] : null;
}

function AppShell() {
  const { user, signOut } = useAuth();
  const [active, setActive] = useState(hashToView);
  // Sidebar collapse — persisted to localStorage so the user's choice survives
  // page reloads. Default expanded.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Phones start collapsed regardless of the stored preference — a 240px
    // rail on a 390px screen leaves no room to work. The toggle still works.
    try {
      if (typeof window !== 'undefined' && window.innerWidth < 768) return true;
      return localStorage.getItem('globalops_sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((cur) => {
      const next = !cur;
      try {
        localStorage.setItem('globalops_sidebar_collapsed', next ? '1' : '0');
      } catch {
        /* localStorage blocked — ignore */
      }
      return next;
    });
  }, []);
  const { allAccounts, opportunitiesThisYear, projectsThisYear, allEngineers, users } = useApp();
  // effective access: role + this person's overrides, live (hooks/useMyAccess)
  const access = useMyAccess();
  const myOpenTasks = useMyOpenTaskCount(!!access && !!access.areas && access.areas.tasks !== undefined && access.areas.tasks !== 'hidden');
  const openTickets = useOpenTicketCount(
    !!access && !!access.areas && access.areas.tickets !== undefined && access.areas.tickets !== 'hidden'
  );
  // The people with an engineer card - internal engineers and interns
  // (data/resourceGroups.js), not archived - so the badge and the Engineers
  // tab's headcount agree (Sharif, 10/16/26). Everyone else is on Resources.
  const internalEngineerCount = (allEngineers || []).filter((e) => !(e.isArchived || e.is_archived) && showsOnCards(e)).length;
  const View = VIEWS[active];

  const navigate = useCallback((next, params) => {
    if (!VIEWS[next]) return;
    setActive(next);
    let suffix = '';
    if (params && typeof params === 'object') {
      const usp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') usp.set(k, String(v));
      });
      const qs = usp.toString();
      if (qs) suffix = `?${qs}`;
    }
    const targetHash = `#/${next}${suffix}`;
    if (window.location.hash !== targetHash) {
      window.history.pushState({ view: next }, '', `${window.location.pathname}${window.location.search}${targetHash}`);
    }
  }, []);

  useEffect(() => {
    if (!window.history.state || !window.history.state.view) {
      const current = hashToView();
      // Preserve any query string after the view (e.g. ?compare=2024,2025
      // or ?project=42) so deep links survive the initial state-attachment.
      const rawHash = window.location.hash;
      const qIdx = rawHash.indexOf('?');
      const querySuffix = qIdx >= 0 ? rawHash.slice(qIdx) : '';
      window.history.replaceState({ view: current }, '', `${window.location.pathname}${window.location.search}#/${current}${querySuffix}`);
    }
    const onPop = () => setActive(hashToView());
    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onPop);
    };
  }, []);

  // View guard — keep every person inside the tabs their EFFECTIVE access
  // shows. A fresh load lands on the hash (often 'dashboard') and a stale deep
  // link could point anywhere; and when someone's access changes while they
  // are on a tab that just became Hidden, the live re-read of their access
  // (AppContext myAccess) lands here too. Either way: redirect to their first
  // allowed tab. Skipped for pending (the pending gate handles them) and until
  // the users registry resolves the role (getRole returns 'pending' for a
  // non-bootstrap user until their row loads).
  useEffect(() => {
    if (!user) return;
    const role = getRole(user.email, users);
    if (role === 'pending') return;
    const dest = redirectFor(access, active);
    if (dest) navigate(dest);
  }, [user, users, access, active, navigate]);

  // Auth gate — no signed-in user means the only thing we ever render is LoginView.
  // Once they sign in (real MSAL later, localStorage stub today), they get the app.
  if (!user) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F3F4F6',
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <LoginView
          onNavigate={(v) => {
            window.location.hash = `#/${v}`;
            setActive(v);
          }}
        />
      </div>
    );
  }

  // Pending-verification gate. A brand-new user is stored as role="pending" and
  // must be assigned a real role by an admin (Team panel) before they see the app.
  // Bootstrap admins (admins.js) always pass. The users list loads async after
  // login, so while it's still empty we treat a non-bootstrap user as pending
  // (show the wait screen) rather than flashing the full shell then yanking it.
  const isBootstrap = isAdmin(user.email);
  const registryEntry = (users || []).find((u) => (u.email || '').toLowerCase() === (user.email || '').toLowerCase());
  const pending = !isBootstrap && (!registryEntry || registryEntry.role === 'pending');
  // a role whose every area was set to Hidden has nothing to show either
  const noAreas = !pending && !firstTab(access);
  if (pending || noAreas) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F3F4F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: "'Montserrat', sans-serif",
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 440, textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1B5E20', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10 }}>
            GlobalOps
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#1B5E20', marginBottom: 12 }}>
            {noAreas ? 'No access yet' : 'Access pending'}
          </div>
          <p style={{ fontSize: 13.5, color: '#555', lineHeight: 1.6, margin: '0 0 18px' }}>
            {noAreas
              ? 'Your access has no areas switched on. Ask whoever manages access in GlobalOps to open one for you.'
              : 'Your account is awaiting approval. An admin will assign your access shortly.'}
          </p>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0D0D0D', marginBottom: 22 }}>{user.email}</div>
          <button
            onClick={signOut}
            style={{
              padding: '10px 22px',
              borderRadius: 8,
              border: '1px solid #1B5E20',
              background: '#fff',
              color: '#1B5E20',
              fontFamily: 'inherit',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const badges = {
    // my open tasks - mine or tagged on, every type (the Dashboard's My tasks)
    tasks: myOpenTasks,
    // tickets still to do (Open + In progress), for whoever has the Tickets area
    tickets: openTickets,
    // companies only (a site is a child row) - the same count as Total Accounts on the page
    crm: allAccounts.filter((a) => !a.parentAccountId).length || 0,
    // open bids only (Budget was folded into Open on 9/29/26)
    opportunities: opportunitiesThisYear.filter((p) => (p.status || '').toLowerCase() === 'open').length || 0,
    // ACTIVE projects: won work that is not closed - the Dashboard's Active
    // Projects card and the Projects page's default Active chip use the same
    // isActiveProject, so the three can't disagree. (It once counted every
    // quote, which made a budgetary bid look like a new project - Paul,
    // acceptance test #49; then every won row, closed ones included.)
    projects: projectsThisYear.filter(isActiveProject).length || 0,
    engineers: internalEngineerCount || 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: '#F3F4F6',
        fontFamily: "'Montserrat', sans-serif",
        overflow: 'hidden',
      }}
    >
      <Sidebar active={active} onNavigate={navigate} badges={badges} collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <Topbar active={active} />

        <div className="app-content-padding" style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
          {/* a tab's page fades in (index.css .go-view-in, opacity only) */}
          <div key={active} className="go-view-in">
            <View onNavigate={navigate} />
          </div>
        </div>
      </div>

      {/* AI assistant — every verified role. The SERVER walls the data per
          role (engineering vs staffing side); pending users never reach here. */}
      {CHAT_ENABLED && <ChatWidget />}
      {/* "Shawn shared the view ... with you" on any screen (round 4) */}
      <SharedViewNotice />
    </div>
  );
}

export default function App() {
  // Public customer view — decided BEFORE the app shell mounts, so none of the
  // shell's plumbing runs (its URL-normalization effect used to rewrite
  // #/svm-view/<token> to #/dashboard, which auto-logged staff with a live
  // MS session into the app instead of showing the shared page). Tracked as
  // state + hashchange so it also works when a share link is pasted into a
  // tab that ALREADY has GlobalOps open (same-document navigation, no boot).
  const [svmToken, setSvmToken] = useState(publicSvmToken);
  useEffect(() => {
    const onHash = () => setSvmToken(publicSvmToken());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  if (svmToken) {
    return <PublicSvmView token={svmToken} />;
  }
  return (
    <AuthProvider>
      <AppProvider>
        <AppShell />
      </AppProvider>
    </AuthProvider>
  );
}
